// Centralized OCR pipeline configuration. Imported by every component
// that talks to the orchestrator (the Contabo-side FastAPI service).
//
// OCR_BASE_URL is read from a Netlify env var so the same code can
// deploy to multiple environments. Configure per deploy context in
// Netlify Site Settings -> Environment variables:
//
//   Production:    VITE_OCR_BASE_URL=https://ocr.maisumhayati-ai.com
//   Branch (dev):  VITE_OCR_BASE_URL=https://ocr-dev.maisumhayati-ai.com
//   Branch (staging): VITE_OCR_BASE_URL=https://ocr-staging.maisumhayati-ai.com
//
// If the env var is missing (e.g., local dev without `.env`), we fall
// back to prod -- that preserves the old hardcoded behavior so nothing
// breaks for developers who haven't set the var yet.

export const OCR_BASE_URL =
  process.env.REACT_APP_OCR_BASE_URL || 'https://ocr.maisumhayati-ai.com';

export const OCR_ENDPOINTS = {
  HEALTH:        `${OCR_BASE_URL}/health`,
  JOBS:          `${OCR_BASE_URL}/jobs`,
  USER_JOBS:     (userId, limit = 20) =>
                   `${OCR_BASE_URL}/jobs?user_id=${encodeURIComponent(userId)}&limit=${limit}`,
  JOB:           (jobId) => `${OCR_BASE_URL}/jobs/${jobId}`,
  PAGE_PNG:      (jobId, n) => `${OCR_BASE_URL}/jobs/${jobId}/pages/${n}.png`,
  PAGE_MD:       (jobId, n) => `${OCR_BASE_URL}/jobs/${jobId}/pages/${n}.md`,
  PAGE_LAYOUT:   (jobId, n) => `${OCR_BASE_URL}/jobs/${jobId}/pages/${n}.layout.jpg`,
  ALL_PAGES:     (jobId) => `${OCR_BASE_URL}/jobs/${jobId}/all-pages`,
  BOOKS:         `${OCR_BASE_URL}/books`,
};

// Status values the orchestrator emits. Used for progress UI and
// for deciding when to stop polling.
export const JOB_STATUSES = {
  QUEUED:          'queued',
  RENDERING:       'rendering',
  OCR_IN_PROGRESS: 'ocr_in_progress',
  OCR_COMPLETE:    'ocr_complete',
  EMBEDDING:       'embedding',
  COMPLETE:        'complete',
  FAILED:          'failed',
  CANCELLED:       'cancelled',
  // Set by this browser only (useOcrJobs), never by the server: the job showed no
  // progress for an hour, so the app stopped asking about it.
  STALLED:         'stalled',
};

export const TERMINAL_STATUSES = [
  JOB_STATUSES.COMPLETE,
  JOB_STATUSES.FAILED,
  JOB_STATUSES.CANCELLED,
  JOB_STATUSES.STALLED,
];

export const isTerminal = (status) =>
  TERMINAL_STATUSES.includes(status);

// Human-friendly label for each status. Caller can override via i18n
// if needed -- these are the English defaults.
export const statusLabel = (status, t = null) => {
  if (t) {
    const key = `bookUpload.status.${status}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const map = {
    queued:          'Queued',
    rendering:       'Rendering pages',
    ocr_in_progress: 'Reading text',
    ocr_complete:    'Indexing',
    embedding:       'Indexing',
    complete:        'Complete',
    failed:          'Failed',
    cancelled:       'Cancelled',
    stalled:         'Stopped responding',
  };
  return map[status] || status;
};